import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Layers, MapPin, Clock, Coins, ArrowRight, Recycle } from "lucide-react";

export default function Foundations() {
  return (
    <Layout>
      <div data-testid="foundations-page" className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24 fade-up">
        <span className="overline text-[var(--cm-primary)]">Under the hood</span>
        <h1
          data-testid="foundations-headline"
          className="font-display font-bold mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          What Bahoi
          <br />
          <span className="italic font-light">is made of.</span>
        </h1>

        <p
          data-testid="foundations-intro"
          className="text-lg md:text-xl text-ink-soft mt-8 leading-relaxed max-w-2xl"
        >
          Bahoi isn't a brand-new idea. It's three older, beautifully proven ideas — woven together
          to help any community, big or small, build a{" "}
          <span className="text-ink font-semibold">circular economy</span> of time, skill, and
          care.
        </p>

        {/* Three components */}
        <section className="mt-14 space-y-6">
          <Component
            testid="component-abcd"
            num="01"
            iconColor="#4A6B53"
            Icon={MapPin}
            title="Asset-Based Community Development"
            shortName="ABCD"
            tagline="Start with what you have, not what you lack."
            body="ABCD flips the usual development question. Instead of asking 'what does this community need?' it asks 'what does this community already have?' — skills, knowledge, networks, places, traditions, time. Coined by John McKnight and John Kretzmann, ABCD insists that every community is already rich in the things that matter most, and that the work is to see, map, and connect those assets."
            inBahoi="The Bahoi village didn't wait for funding. They mapped what they had — a reef, dive knowledge, boats, hospitality, fish — and built the scuba experience from that. We do the same digitally: every member's time and skills are assets we make visible."
          />
          <Component
            testid="component-timebanking"
            num="02"
            iconColor="#D96C4A"
            Icon={Clock}
            title="Time Banking"
            shortName="Time Banking"
            tagline="One hour is one hour, no matter whose."
            body="Time banking was articulated by civil-rights attorney Edgar Cahn in the 1980s. Its premise is radical and simple: an hour of any person's time is worth the same as anyone else's. A surgeon's hour and a teenager's hour both count as one Time Hour. It dissolves the hierarchy of 'market value' and replaces it with the dignity of reciprocity — every contribution counts, every member can both give and receive."
            inBahoi="When the organizer verifies your shift, you earn 1 Time Hour for every hour worked — whether you tutored a child, fixed a fence, or restored a reef. Those hours sit in your balance, ready to spend."
          />
          <Component
            testid="component-currency"
            num="03"
            iconColor="#B37A2C"
            Icon={Coins}
            title="Community Currency"
            shortName="Community Currency"
            tagline="Value that circulates here, not out."
            body="A community currency is money designed to keep moving inside a defined group — a village, a neighborhood, a network. Examples like Ithaca HOURS, the Bristol Pound, and BerkShares show that when value circulates locally, more of it stays with the people who created it. Community currencies aren't a replacement for national money; they're a parallel layer that strengthens the local fabric."
            inBahoi="Time Hours are our community currency. They have no exchange rate, can't be cashed out, and only have value among members. That's the point: every hour earned stays in motion within Bahoi, supporting the next person and the next."
          />
        </section>

        {/* How they tie together */}
        <section className="mt-20" data-testid="foundations-loop">
          <span className="overline text-[var(--cm-primary)]">How they come together</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
            One loop, three turns.
          </h2>
          <p className="text-ink-soft mt-4 max-w-2xl leading-relaxed">
            Each idea on its own is powerful. Stacked, they form a self-sustaining cycle. ABCD
            tells us <em>what's here</em>. Time banking gives us <em>a fair way to measure it</em>.
            Community currency lets it <em>circulate</em>. The result is a circular economy that
            doesn't extract from anyone and doesn't leave anyone out.
          </p>

          <LoopDiagram />

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <LoopStep step="See" body="Map the skills, time, and care already in the community (ABCD)." color="#4A6B53" />
            <LoopStep step="Measure" body="Give every hour the same honest unit of value (time banking)." color="#D96C4A" />
            <LoopStep step="Move" body="Let those hours circulate among members, not out of the community (community currency)." color="#B37A2C" />
          </div>

          <div className="mt-8 card-soft p-6 md:p-8 flex items-start gap-4">
            <span
              className="h-10 w-10 rounded-full grid place-items-center text-white flex-shrink-0"
              style={{ background: "var(--cm-secondary)" }}
            >
              <Recycle className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-display font-semibold text-lg">A circular economy, big or small.</h3>
              <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                A neighborhood book club, a coastal village in Sulawesi, a city of two million —
                the model scales because the principle is the same. The economy of generosity
                isn't a slogan; it's a structure.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20">
          <div
            data-testid="foundations-cta"
            className="rounded-3xl bg-[var(--cm-ink)] text-[var(--cm-surface)] px-8 md:px-12 py-12 md:py-14 flex flex-wrap gap-6 items-center justify-between"
          >
            <div className="max-w-md">
              <Layers className="h-5 w-5 mb-3 text-[var(--cm-accent)]" />
              <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                Old ideas. New community.
              </h3>
              <p className="text-white/70 mt-2 text-sm">
                We didn't invent any of these. We just believe they belong together — and that
                your community deserves the loop.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="foundations-read-story-btn"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              Read our origin story <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Component({ num, Icon, iconColor, title, shortName, tagline, body, inBahoi, testid }) {
  return (
    <div data-testid={testid} className="card-soft p-6 md:p-8">
      <div className="flex items-start gap-5 flex-wrap">
        <span
          className="h-14 w-14 rounded-2xl grid place-items-center text-white flex-shrink-0"
          style={{ background: iconColor }}
        >
          <Icon className="h-6 w-6" />
        </span>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="overline text-ink-soft">{num}</span>
            <span className="overline text-[var(--cm-primary)]">{shortName}</span>
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mt-1 tracking-tight">{title}</h3>
          <p className="font-display italic text-lg text-ink-soft mt-2">{tagline}</p>
        </div>
      </div>
      <p className="mt-5 leading-relaxed text-ink">{body}</p>
      <div className="mt-5 pl-4 border-l-2 border-[var(--cm-primary)]/40">
        <span className="overline text-[var(--cm-primary)]">In Bahoi</span>
        <p className="text-ink-soft text-sm mt-1.5 leading-relaxed">{inBahoi}</p>
      </div>
    </div>
  );
}

function LoopStep({ step, body, color }) {
  return (
    <div className="card-soft p-5">
      <span
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white"
        style={{ background: color }}
      >
        {step}
      </span>
      <p className="text-sm text-ink-soft mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

function LoopDiagram() {
  // Geometry
  const cx = 250;
  const cy = 230;
  const radius = 140;
  const nodeRadius = 46;
  const arcGap = 22; // degrees around each node where the arrow doesn't draw

  const point = (angleDeg) => {
    const r = (angleDeg * Math.PI) / 180;
    return [cx + radius * Math.sin(r), cy - radius * Math.cos(r)];
  };

  const nodes = [
    { angle: 0, label: "See", num: "01", color: "#4A6B53" },
    { angle: 120, label: "Measure", num: "02", color: "#D96C4A" },
    { angle: 240, label: "Move", num: "03", color: "#B37A2C" },
  ];

  const arcs = nodes.map((n, i) => {
    const next = nodes[(i + 1) % nodes.length];
    const [x1, y1] = point(n.angle + arcGap);
    const [x2, y2] = point(next.angle - arcGap);
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  });

  return (
    <div className="mt-10 mb-4 flex justify-center">
      <svg
        viewBox="0 0 500 460"
        className="w-full max-w-md md:max-w-lg"
        data-testid="foundations-diagram"
        aria-label="Circular economy loop: See, Measure, Move"
      >
        <defs>
          <marker
            id="loop-arrow"
            markerWidth="10"
            markerHeight="10"
            refX="6"
            refY="5"
            orient="auto-start-reverse"
          >
            <polygon points="0 0, 8 5, 0 10" fill="rgba(30,45,36,0.55)" />
          </marker>
        </defs>

        {/* Curved arrows */}
        {arcs.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="rgba(30,45,36,0.35)"
            strokeWidth="2"
            strokeLinecap="round"
            markerEnd="url(#loop-arrow)"
          />
        ))}

        {/* Center label */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          letterSpacing="3"
          fill="rgba(92,107,97,0.95)"
          fontFamily="Manrope, sans-serif"
        >
          CIRCULAR
        </text>
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          letterSpacing="3"
          fill="rgba(92,107,97,0.95)"
          fontFamily="Manrope, sans-serif"
        >
          ECONOMY
        </text>

        {/* Nodes */}
        {nodes.map((n) => {
          const [x, y] = point(n.angle);
          return (
            <g key={n.label}>
              <circle cx={x} cy={y} r={nodeRadius} fill={n.color} />
              <circle
                cx={x}
                cy={y}
                r={nodeRadius - 4}
                fill="none"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="1.5"
              />
              <text
                x={x}
                y={y - 2}
                textAnchor="middle"
                fontSize="20"
                fontWeight="700"
                fill="white"
                fontFamily="Outfit, sans-serif"
              >
                {n.label}
              </text>
              <text
                x={x}
                y={y + 16}
                textAnchor="middle"
                fontSize="9"
                letterSpacing="2"
                fontWeight="700"
                fill="rgba(255,255,255,0.75)"
                fontFamily="Manrope, sans-serif"
              >
                {n.num}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
